import React from 'react';
import {
  Box, Button, Chip, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PageHeader } from 'dashboard/components/common';

/**
 * Top toolbar of the page editor.
 *
 * Title is editable inline on blur (saves only on real change). Status
 * chip reflects draft/published. Right-side buttons: preview toggle,
 * settings drawer, save-as-draft, publish.
 *
 * It renders into the section's bar rather than drawing a row of its own.
 * Every other screen puts its name and its Publish button up there, and this
 * one — the screen where you actually publish — was drawing a second header
 * underneath an empty bar, which read as two headers for one page. The name
 * goes through `leading` rather than `title` because here it is an editable
 * field, not a label.
 */
export default function PageEditorToolbar({
  page,
  savingTitle,
  showPreview,
  dirty, savingDraft,
  onBack, onTitleChange, onTogglePreview, onOpenSettings, onSaveStatus,
  onSaveDraft, onDiscard,
}) {
  const leading = (
    <>
      <IconButton onClick={onBack} aria-label='Back to pages'>
        <ChevronLeftIcon />
      </IconButton>
      <Box sx={{ minWidth: 0 }}>
        <TextField
          variant='standard'
          defaultValue={page.title}
          onBlur={(e) => {
            if (e.target.value && e.target.value !== page.title) onTitleChange(e.target.value);
          }}
          disabled={savingTitle}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3 },
          }}
          sx={{ '& .MuiInputBase-input': { p: 0 } }}
        />
        <Typography variant='caption' color='text.secondary' display='block' lineHeight={1.3}>
          /{page.slug}
        </Typography>
      </Box>
    </>
  );

  /**
   * One line: the two status chips and then the four controls.
   *
   * They were stacked two-high — chips over buttons — from when this was a
   * row inside the page and had a page's width to fill. In the bar that
   * second line makes the bar taller, and the bar's height is also the space
   * below the account cluster, so a wrapped header pushed the panel down the
   * screen. Still wraps on a phone, where nothing else is possible.
   */
  const actions = (
    <>
      <Stack
        direction='row'
        spacing='6px'
        alignItems='center'
        justifyContent='flex-end'
        flexWrap='wrap'
        useFlexGap
        sx={{ rowGap: '6px' }}
      >
        {page.isHomepage && <Chip size='small' color='info' label='HOMEPAGE' />}
        <Chip
          size='small'
          color={page.status === 'published' ? 'success' : 'default'}
          label={page.status}
        />
        <IconButton
          size='small'
          onClick={onTogglePreview}
          color={showPreview ? 'primary' : 'default'}
          // Icon-only below sm: "Hide preview" is the longest label here and
          // the eye already says it.
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
          aria-label={showPreview ? 'Hide preview' : 'Preview'}
        >
          <VisibilityIcon fontSize='small' />
        </IconButton>
        <Button
          size='small'
          variant={showPreview ? 'contained' : 'outlined'}
          color={showPreview ? 'primary' : 'inherit'}
          startIcon={<VisibilityIcon />}
          onClick={onTogglePreview}
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
        >
          {showPreview ? 'Hide preview' : 'Preview'}
        </Button>
        <Button size='small' onClick={onOpenSettings}>Settings</Button>

        {/* Shown only when there is something to save or throw away.
            Permanently-present buttons that do nothing most of the time teach
            people to ignore them, and these two are the pair that says
            whether the work on screen exists anywhere else yet. */}
        {dirty && (
          <>
            <Button
              size='small'
              onClick={onDiscard}
              disabled={savingDraft}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              size='small'
              variant='contained'
              color='primary'
              onClick={onSaveDraft}
              disabled={savingDraft}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {savingDraft ? 'Saving…' : 'Save draft'}
            </Button>
          </>
        )}
        {/* "Draft" was a shorter label for a destructive action: on a
            published page it takes the page off the live site, and next to
            "Publish" it read as a mode switch rather than a warning. The
            label now says what happens, and unpublishing something visitors
            can currently see asks first. */}
        <Button
          size='small'
          variant='outlined'
          color={page.status === 'published' ? 'warning' : 'inherit'}
          onClick={() => {
            if (page.status === 'published') {
              if (!window.confirm(
                `Take "${page.title}" off your live site?\n\nVisitors will stop seeing it until you publish again.`,
              )) return;
            }
            onSaveStatus('draft');
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {page.status === 'published' ? 'Unpublish' : 'Keep as draft'}
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={() => onSaveStatus('published')}
          sx={{ whiteSpace: 'nowrap' }}
        >
        Publish
      </Button>
      </Stack>
    </>
  );

  return <PageHeader leading={leading} actions={actions} />;
}
